import { Injectable } from '@angular/core';

// Black list of invalid callers
const INVALID_CALLER = [];

interface SpaInteraction {
  tag: string;
  interaction: newrelic.BrowserInteraction;
  callback: () => void;
}

/**
 * Parameters for actions to be logged to New Reclic
 * Attributes should respect the "reserved words" found here:
 * https://docs.newrelic.com/docs/insights/insights-data-sources/custom-data/insights-custom-data-requirements-limits#reserved-words
 *
 * @export
 * @interface ActionParams
 */
export interface ActionParams {
  [key: string]: any;
  first?: number;
  time?: number;
  timeOffset?: number;
}
/**
 * Service to handle interactions and logging events with New Relic
 *
 * @export
 * @class NewRelicService
 */
@Injectable()
export class NewRelicService {
  private spaInteraction: SpaInteraction;

  private isLoggable(caller): boolean {
    return INVALID_CALLER.indexOf(caller) === -1;
  }

  private getEpochTime(params: ActionParams, performanceTime: number): number {
    return params.time - params.timeOffset + performanceTime;
  }

  private waitForNewRelic(callback: () => void): void {
    try {
      callback();
    } catch (e) {
      if (e instanceof ReferenceError && e.message.includes('newrelic')) {
        setTimeout(() => {
          this.waitForNewRelic(callback);
        }, 10);
      } else {
        throw e;
      }
    }
  }

  /**
   * addToTrace adds the information to session traces, might be better for actual JS functions
   *   as it shows up as a slow function if it takes over 300ms
   * addPageAction adds action with params that can be queried.
   */
  private logEvent(msg: string, caller: string, params: ActionParams) {
    if (this.isLoggable(caller)) {
      const traceData: newrelic.EventObject = {
        name: msg,
        start: params.first ? this.getEpochTime(params, params.first) : params.time,
        end: params.first ? this.getEpochTime(params, params.last) : params.time
      };
      params.caller = caller;
      this.waitForNewRelic(() => {
        newrelic.addToTrace(traceData);
        newrelic.addPageAction(msg, params);
      });
    }
  }

  /**
   * Logs an event to New Relic
   *
   * @param {string} msg The message to log
   * @param {string} caller The calling component/service/class
   * @param {ActionParams} params Extra parameters to be logged
   * @memberof NewRelicService
   */
  public log(msg: string, caller: string, params: ActionParams) {
    this.logEvent(msg, caller, params);
  }

  private createInteraction(tag: string) {
    if (!this.spaInteraction) {
      this.waitForNewRelic(() => {
        const interaction = newrelic.interaction();
        interaction.setName(tag);
        this.spaInteraction = {
          tag: tag,
          interaction: interaction,
          callback: interaction.createTracer(tag)
        };
      });
    }
  }

  /**
   * Creates tracer that show up in the page views, this record everything happening
   * between the call of createTracer & when the callback is called. Only one interaction
   * can be active.
   * @param tag unique identifier for this interaction
   */
  public startInteraction(tag: string) {
    this.createInteraction(tag);
  }

  /** Completes an interaction - only if the tag is the same as the current interaction
   * @param tag unique identifier for this interaction
   */
  public finishInteraction(tag: string) {
    if (this.spaInteraction && this.spaInteraction.tag === tag) {
      if (this.spaInteraction.callback) {
        this.spaInteraction.callback();
      }
      this.spaInteraction.interaction.end().save();
      this.spaInteraction = null;
    }
  }

  /**
   * Groups page views to help URL structure or to capture the URL's routing information.
   * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/set-pageview-name
   *
   * @param {string} name
   * @param {string} [host]
   * @memberof NewRelicService
   */
  public setPageViewName(name: string, host?: string) {
    this.waitForNewRelic(() => {
      newrelic.setPageViewName(name, host);
    });
  }

  /**
   * Gives SPA routes more accurate names than default names. Monitors specific routes rather than by default grouping.
   * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/spa-set-current-route-name
   *
   * @param {string} name
   * @memberof NewRelicService
   */
  public setCurrentRouteName(name: string) {
    this.waitForNewRelic(() => {
      newrelic.setCurrentRouteName(name);
    });
  }
}
